import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Form, Alert, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Context } from '../index';
import { fetchOneProduct, createRating, updateVariant, deleteVariant, fetchStores } from '../http/productAPI';
import { observer } from 'mobx-react-lite';
import { FaStar } from 'react-icons/fa';
import { deleteRating } from '../http/ratingAPI';
import '../styles/ProductPage.css';
import { useToast } from '../contexts/ToastContext';
import { getImageUrl, buildPictureSources, getThumbUrl } from '../utils/imageUtils';

const ProductPage = observer(() => {
    const { user } = useContext(Context);
    const { showToast } = useToast();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [adminVariantQuantity, setAdminVariantQuantity] = useState(0);
    const [adminVariantStoreId, setAdminVariantStoreId] = useState('');
    const [adminVariantSaving, setAdminVariantSaving] = useState(false);
    const [allStores, setAllStores] = useState([]);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [ratingError, setRatingError] = useState('');
    const [ratingSuccess, setRatingSuccess] = useState('');
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(-1);
    const [sliderIdx, setSliderIdx] = useState(0);
    const [sortBy, setSortBy] = useState('store'); // store, size, color, quantity
    const [sortOrder, setSortOrder] = useState('asc');

    const canAdminEdit = !!user?.user && user.user.role === 'main_admin';
    const selectedVariant = selectedVariantIdx >= 0 ? product?.variants?.[selectedVariantIdx] ?? null : null;
    const selectedVariantId = selectedVariant?.id ?? null;

    const renderResponsivePicture = (imgPath, width = 900, imgProps = {}) => {
        const { avif, webp, fallback, staticUrl } = buildPictureSources(imgPath, width);
        const safeFallback = fallback || staticUrl || getImageUrl(imgPath);
        return (
            <picture>
                {avif && <source srcSet={avif} type="image/avif" />}
                {webp && <source srcSet={webp} type="image/webp" />}
                <img
                    src={safeFallback}
                    loading="lazy"
                    decoding="async"
                    alt={imgProps.alt || 'Фото товара'}
                    {...imgProps}
                    onError={e => {
                        const el = e.currentTarget;
                        const staticFallback = staticUrl || getImageUrl(imgPath);
                        if (staticFallback && el.src !== staticFallback && !el.dataset.staticFallback) {
                            el.dataset.staticFallback = '1';
                            el.src = staticFallback;
                            return;
                        }
                        el.onerror = null;
                        el.src = '/logo192.PNG';
                    }}
                />
            </picture>
        );
    };

    const getThumbSrc = (imgPath, width = 160) => {
        const thumb = getThumbUrl(imgPath, width, 'orig');
        return thumb || getImageUrl(imgPath);
    };

    const refreshProduct = useCallback(async () => {
        setLoading(true);
        try {
            const updated = await fetchOneProduct(id);
            setProduct(updated);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        setSelectedVariantIdx(-1);
        fetchOneProduct(id)
            .then(data => {
                // Логируем variants для отладки
                setProduct(data);
                setLoading(false);
            })
            .catch(e => {
                setError('Ошибка при загрузке товара');
                setLoading(false);
            });
        // === ПАТЧ: слушаем BroadcastChannel для авто-обновления ===
        const bc = new window.BroadcastChannel('product_update');
        bc.onmessage = (event) => {
            if (event.data && event.data.productId != null && String(event.data.productId) === String(id)) {
                setSelectedVariantIdx(-1);
                refreshProduct();
            }
        };
        return () => bc.close();
    }, [id, refreshProduct]);

    // Синхронизируем поля админ-редактирования с выбранным вариантом.
    useEffect(() => {
        if (!canAdminEdit || !selectedVariant) return;
        setAdminVariantQuantity(Math.max(0, Math.trunc(Number(selectedVariant.quantity ?? 0) || 0)));
        setAdminVariantStoreId(selectedVariant.storeId ?? '');
    }, [
        canAdminEdit,
        selectedVariant?.id,
        selectedVariant?.quantity,
        selectedVariant?.storeId
    ]);

    // Для админ-редактирования даем выпадашку по всем магазинам, а не только "привязанные" к товару.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!canAdminEdit) return;
            try {
                const stores = await fetchStores();
                if (!cancelled) setAllStores(Array.isArray(stores) ? stores : []);
            } catch (e) {
                // Если список магазинов не загрузился — оставляем как есть (fallback будет пустой список)
                if (!cancelled) setAllStores([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canAdminEdit]);

    const handleAdminSaveVariant = async () => {
        if (!canAdminEdit || !selectedVariant || !selectedVariantId) return;
        const prevVariantId = selectedVariantId;

        setAdminVariantSaving(true);
        try {
            await updateVariant(prevVariantId, {
                productId: id,
                type: product?.type,
                quantity: adminVariantQuantity,
                storeId: adminVariantStoreId === '' ? null : adminVariantStoreId
            });

            showToast('Вариант обновлён', 'success');

            const updated = await fetchOneProduct(id);
            setProduct(updated);

            const nextIdx = Array.isArray(updated?.variants)
                ? updated.variants.findIndex(v => v && String(v.id) === String(prevVariantId))
                : -1;
            setSelectedVariantIdx(nextIdx);
        } catch (e) {
            showToast(e?.response?.data?.message || 'Ошибка при обновлении варианта', 'error');
        } finally {
            setAdminVariantSaving(false);
        }
    };

    const handleAdminDeleteVariant = async () => {
        if (!canAdminEdit || !selectedVariant || !selectedVariantId) return;
        const prevVariantId = selectedVariantId;

        if (!window.confirm('Удалить выбранный вариант товара?')) return;

        setAdminVariantSaving(true);
        try {
            await deleteVariant(prevVariantId, {
                productId: id,
                type: product?.type
            });

            showToast('Вариант удалён', 'success');

            const updated = await fetchOneProduct(id);
            setProduct(updated);
            setSelectedVariantIdx(-1);
        } catch (e) {
            showToast(e?.response?.data?.message || 'Ошибка при удалении варианта', 'error');
        } finally {
            setAdminVariantSaving(false);
        }
    };

    const canSaveAdminVariant = (() => {
        if (!canAdminEdit || !selectedVariant) return false;
        if (!product?.type) return false;
        if (product.type !== 'toy' && (adminVariantStoreId === '' || adminVariantStoreId == null)) return false;
        return true;
    })();

    const handleAdminDeleteRating = async (ratingId) => {
        if (!canAdminEdit) return;
        if (!ratingId) return;
        if (!window.confirm('Удалить отзыв/комментарий?')) return;

        try {
            await deleteRating(ratingId);
            showToast('Отзыв удалён', 'success');
            const updated = await fetchOneProduct(id);
            setProduct(updated);
        } catch (e) {
            showToast(e?.response?.data?.message || 'Ошибка при удалении отзыва', 'error');
        }
    };

    const handleAddToBasket = async () => {
        if (!user.isAuth) {
            navigate('/login');
            return;
        }
        const needsVariantSelection = (product.type === 'shoe' || product.type === 'clothes' || product.type === 'toy');
        if (needsVariantSelection && selectedVariantIdx === -1 && (product.variants || []).length > 1) {
            setError('Пожалуйста, выберите вариант товара');
            return;
        }
        // Получаем выбранный вариант
        let variant = null;
        if (product.variants && product.variants.length > 0) {
            if (selectedVariantIdx >= 0 && product.variants[selectedVariantIdx]) {
                // Используем выбранный вариант
                variant = product.variants[selectedVariantIdx];
            } else if (product.variants.length === 1) {
                // Если только один вариант, используем его
                variant = product.variants[0];
            } else {
                // Если вариантов больше одного, но не выбран - ошибка
                setError('Пожалуйста, выберите вариант товара');
                return;
            }
        }
        
        // Проверяем количество выбранного варианта
        // ВАЖНО: для обуви/одежды/игрушек количество варианта — это variant.quantity.
        // На проде после импорта накладной у вариантов может приходить product_stores (остаток по товару в магазине),
        // и суммирование product_stores раздувает доступное кол-во в 2+ раза.
        const variantQty = variant
            ? (variant.quantity ?? (Array.isArray(variant.product_stores) && variant.product_stores.length > 0
                ? variant.product_stores.reduce((s, ps) => s + (ps.quantity || 0), 0)
                : 0))
            : stock;
        if (variant && variantQty <= 0) {
            setError('Выбранный вариант товара отсутствует в наличии');
            return;
        }
        if (quantity > (variant ? variantQty : stock)) {
            setError(`Доступно только ${variant ? variantQty : stock} шт.`);
            return;
        }
        
        let store_id = null;
        let store_name = null;
        let variantForBasket = variant;
        if (product.type === 'toy') {
            if (variant) {
                store_id = variant.storeId || (product.product_stores?.[0]?.storeId ?? null);
                if (variant.storeName) {
                    store_name = variant.storeName;
                } else if (store_id && product.stores && Array.isArray(product.stores)) {
                    const storeObj = product.stores.find(s => s.id === store_id);
                    if (storeObj) store_name = storeObj.address || storeObj.name;
                }
                variantForBasket = { ...variant, storeId: store_id, storeName: store_name };
            } else if (Array.isArray(product.product_stores) && product.product_stores.length > 0) {
                // Фоллбек на старый формат
                const ps = product.product_stores[0];
                store_id = ps.storeId;
                if (ps.store && (ps.store.address || ps.store.name)) {
                    store_name = ps.store.address || ps.store.name;
                } else if (product.stores && Array.isArray(product.stores)) {
                    const storeObj = product.stores.find(s => s.id === store_id);
                    if (storeObj) store_name = storeObj.address || storeObj.name;
                }
                variantForBasket = { storeId: store_id, storeName: store_name, quantity: product.toy?.quantity || ps.quantity || 0 };
            }
        } else {
            store_id = variant?.storeId || null;
            if (variant && variant.storeName) {
                store_name = variant.storeName;
            } else if (store_id && product.stores && Array.isArray(product.stores)) {
                const storeObj = product.stores.find(s => s.id === store_id);
                if (storeObj) {
                    store_name = storeObj.address || storeObj.name;
                }
            }
        }
        const maxQty = variant ? variantQty : stock;
        user.addToBasket(product, variantForBasket, quantity, store_id, maxQty);
        setError('');
        showToast('Товар добавлен в корзину', 'success');
        await refreshProduct();
    };

    const handleRatingSubmit = async (e) => {
        e.preventDefault();
        if (!user.isAuth) {
            navigate('/login');
            return;
        }
        if (rating === 0) {
            setRatingError('Пожалуйста, выберите оценку');
            return;
        }
        try {
            await createRating(product.id, rating, comment);
            setRatingSuccess('Спасибо за ваш отзыв!');
            setRatingError('');
            setRating(0);
            setComment('');
            // Обновляем данные продукта
            const updatedProduct = await fetchOneProduct(id);
            setProduct(updatedProduct);
        } catch (e) {
            console.error('Ошибка при отправке отзыва:', e);
            setRatingError(e.response?.data?.message || 'Ошибка при отправке отзыва');
        }
    };

    // Функция для получения storeName по storeId (полный адрес для данных)
    const getStoreName = (variant) => {
        if (variant.storeName) return variant.storeName;
        if (variant.storeId && product && Array.isArray(product.stores)) {
            const foundStore = product.stores.find(s => s.id === variant.storeId);
            if (foundStore) return foundStore.address || foundStore.name || '-';
        }
        return '-';
    };

    // Краткое отображение адреса для таблицы (ул. Рыбалко, 91)
    const getStoreShort = (variant) => {
        const full = getStoreName(variant);
        if (!full || full === '-') return '-';
        const m = full.match(/(?:улица|ул\.?)\s*([^,]+),?\s*(\d+)/i);
        return m ? `ул. ${m[1].trim()}, ${m[2]}` : full;
    };

    const handleSort = (col) => {
        if (sortBy === col) {
            setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortOrder('asc');
        }
    };

    const sortedVariantsWithIdx = useMemo(() => {
        const vars = product?.variants || [];
        if (vars.length === 0) return [];
        const withIdx = vars.map((v, i) => ({ variant: v, originalIdx: i }));
        const getVal = (item, col) => {
            const v = item.variant;
            switch (col) {
                case 'store': return (getStoreName(v) || '').toLowerCase();
                case 'size': return (v.size || '').toString().toLowerCase();
                case 'color': return (v.color || '').toLowerCase();
                case 'quantity': return (v.quantity ?? 0);
                case 'name': return (v.name || '').toLowerCase();
                default: return '';
            }
        };
        return [...withIdx].sort((a, b) => {
            const va = getVal(a, sortBy);
            const vb = getVal(b, sortBy);
            let cmp = 0;
            if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
            else cmp = String(va).localeCompare(String(vb), 'ru');
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [product?.variants, sortBy, sortOrder, product?.stores]);

    const stockMemo = useMemo(() => {
        if (!product) return 0;
        const getVariantQty = (v) => {
            // См. выше: для вариантов (size/color/store) важнее quantity.
            if (v?.quantity !== undefined && v?.quantity !== null) return v.quantity;
            if (Array.isArray(v?.product_stores) && v.product_stores.length > 0) {
                return v.product_stores.reduce((s, ps) => s + (ps.quantity || 0), 0);
            }
            return 0;
        };
        if (product.type === 'toy' && Array.isArray(product.variants) && product.variants.length > 0) {
            if (selectedVariantIdx >= 0 && product.variants[selectedVariantIdx]) {
                return product.variants[selectedVariantIdx].quantity || 0;
            }
            const v = product.variants.find(x => (x.quantity || 0) > 0);
            return v ? v.quantity : 0;
        }
        if ((product.type === 'shoe' || product.type === 'clothes') && Array.isArray(product.variants) && product.variants.length > 0) {
            if (selectedVariantIdx >= 0 && product.variants[selectedVariantIdx]) {
                return getVariantQty(product.variants[selectedVariantIdx]);
            }
            const v = product.variants.find(x => getVariantQty(x) > 0);
            return v ? getVariantQty(v) : 0;
        }
        if (Array.isArray(product.product_stores) && product.product_stores.length > 0) {
            return product.product_stores.reduce((sum, ps) => sum + (ps.quantity || 0), 0);
        }
        if (product.type === 'toy') return product.quantity ?? product.toy?.quantity ?? product.stock ?? 0;
        return product.stock ?? 0;
    }, [product, selectedVariantIdx]);

    useEffect(() => {
        if (stockMemo > 0 && quantity > stockMemo) setQuantity(stockMemo);
    }, [stockMemo, quantity]);

    const handlePrev = () => setSliderIdx(idx => (idx - 1 + images.length) % images.length);
    const handleNext = () => setSliderIdx(idx => (idx + 1) % images.length);

    if (loading) {
        return (
            <div className="product-page-loading">
                <div className="product-page-loading-overlay" />
                <div className="product-page-loading-inner">
                    <div className="product-page-loading-spinner" />
                    <div className="product-page-loading-text">Загружаем товар…</div>
                    <div className="product-page-loading-subtext">
                        Проверяем наличие, цены и варианты. Пожалуйста, подождите.
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return <div>Товар не найден</div>;
    }

    let images = [];
    if (Array.isArray(product.images) && product.images.length > 0) {
        images = product.images.filter(Boolean);
    } else if (typeof product.images === 'string' && product.images) {
        try {
            const parsed = JSON.parse(product.images);
            if (Array.isArray(parsed)) {
                images = parsed.filter(Boolean);
            } else if (typeof parsed === 'string') {
                images = [parsed];
            }
        } catch {
            images = [product.images];
        }
    } else if (product.image_url) {
        images = [product.image_url];
    } else {
        images = [];
    }
    // Убираем пустые/undefined значения
    images = images.filter(img => !!img);

    const stock = stockMemo;
    const isOutOfStock = stock <= 0;

    const getVariantQty = (v) => {
        if (Array.isArray(v?.product_stores) && v.product_stores.length > 0) {
            return v.product_stores.reduce((s, ps) => s + (ps.quantity || 0), 0);
        }
        return v?.quantity ?? 0;
    };

    return (
        <Container className="product-page mt-4">
            <Row>
                <Col lg={5} md={12}>
                    <div className="product-image-container" style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: 0,
                        padding: 0,
                        border: 'none',
                        minHeight: 0
                    }}>
                        {images.length > 0 && (
                            <div style={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: 500,
                                minHeight: 500,
                                height: 500,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                background: '#fff',
                                borderRadius: 12,
                                overflow: 'hidden'
                            }}>
                                {renderResponsivePicture(images[sliderIdx], 900, {
                                    className: 'product-page-image',
                                    alt: product.name,
                                    style: { width: '100%', height: '100%', objectFit: 'contain', aspectRatio: '1/1' }
                                })}
                                {/* Стрелки */}
                                {images.length > 1 && (
                                    <>
                                        <button
                                            className="slider-arrow left"
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: 10,
                                                transform: 'translateY(-50%)',
                                                background: 'rgba(0,0,0,0.3)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: 36,
                                                height: 36,
                                                fontSize: 22,
                                                cursor: 'pointer',
                                                zIndex: 2
                                            }}
                                            onClick={handlePrev}
                                            aria-label="Предыдущее фото"
                                        >&lt;</button>
                                        <button
                                            className="slider-arrow right"
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: 10,
                                                transform: 'translateY(-50%)',
                                                background: 'rgba(0,0,0,0.3)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: 36,
                                                height: 36,
                                                fontSize: 22,
                                                cursor: 'pointer',
                                                zIndex: 2
                                            }}
                                            onClick={handleNext}
                                            aria-label="Следующее фото"
                                        >&gt;</button>
                                    </>
                                )}
                                {/* Точки */}
                                {images.length > 1 && (
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 8 }}>
                                        {images.map((img, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    display: 'inline-block',
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    background: sliderIdx === idx ? '#007bff' : '#ccc',
                                                    cursor: 'pointer',
                                                    border: '1px solid #888'
                                                }}
                                                onClick={() => setSliderIdx(idx)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Мобильная адаптация */}
                    <style>{`
                        @media (max-width: 768px) {
                            .product-image-container {
                                minHeight: 220px !important;
                            }
                            .product-image {
                                max-width: 100% !important;
                                max-height: 220px !important;
                                min-height: 220px !important;
                                height: 220px !important;
                                object-fit: cover !important;
                            }
                            .slider-arrow.left, .slider-arrow.right {
                                width: 32px !important;
                                height: 32px !important;
                                font-size: 18px !important;
                            }
                        }
                    `}</style>
                    {/* Миниатюры */}
                    {images.length > 1 && (
                        <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
                            {images.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={getThumbSrc(img, 180)}
                                    alt={`thumb-${idx}`}
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                        width: 64,
                                        height: 64,
                                        objectFit: 'cover',
                                        borderRadius: 8,
                                        border: sliderIdx === idx ? '2px solid #007bff' : '1px solid #ccc',
                                        cursor: 'pointer',
                                        boxShadow: sliderIdx === idx ? '0 0 8px #007bff55' : 'none',
                                        opacity: sliderIdx === idx ? 1 : 0.7,
                                        transition: 'all 0.2s',
                                        background: '#fff',
                                        padding: 2
                                    }}
                                    onClick={() => setSliderIdx(idx)}
                                />
                            ))}
                        </div>
                    )}
                </Col>
                <Col lg={7} md={12}>
                    <div className="product-info">
                        <h1 className="product-title">{product.name}</h1>
                        <div className="product-type">
                            {product.type === 'shoe' ? 'Обувь' : 
                             product.type === 'toy' ? 'Игрушка' : 
                             product.type === 'clothes' ? 'Одежда' : product.type}
                        </div>
                        <div className="product-price">
                            {product.discount > 0 ? (
                                <div className="product-price-with-discount">
                                    <div className="discounted-price-main">
                                        {(product.price * (1 - product.discount / 100)).toLocaleString()} ₽
                                    </div>
                                    <div className="original-price-main">
                                        {product.price.toLocaleString()} ₽
                                    </div>
                                    <div className="discount-badge-main">
                                        -{product.discount}%
                                    </div>
                                </div>
                            ) : (
                                <span className="regular-price">{product.price.toLocaleString()} ₽</span>
                            )}
                        </div>
                        {/* Отображение артикула для обуви и одежды */}
                        {(product.type === 'shoe' || product.type === 'clothes') && (
                            (product.type === 'shoe' && product.article) || 
                            (product.type === 'clothes' && product.clothes?.length > 0 && product.clothes[0].article)
                        ) && (
                            <div className="product-article mb-3">
                                <strong>Артикул:</strong> {product.type === 'shoe' ? product.article : product.clothes[0].article}
                            </div>
                        )}
                        {/* Добавляем отображение материала для одежды */}
                        {product.type === 'clothes' && product.clothes?.length > 0 && product.clothes[0].material && (
                            <div className="product-material mb-3">
                                <strong>Материал:</strong> {product.clothes[0].material}
                            </div>
                        )}
                        {/* Добавляем отображение материала и возрастных ограничений для игрушек */}
                        {product.type === 'toy' && (
                            <div className="toy-details mb-3">
                                {product.toy?.material && (
                                    <div className="product-material mb-2">
                                        <strong>Материал:</strong> {product.toy.material}
                                    </div>
                                )}
                                {product.toy?.age_rating && (
                                    <div className="product-age-rating mb-2">
                                        <strong>Возрастные ограничения:</strong> {product.toy.age_rating}
                                    </div>
                                )}
                            </div>
                        )}
                        {product.description && (
                            <div className="product-description mb-3">
                                <h4>Описание товара:</h4>
                                <p>{product.description}</p>
                            </div>
                        )}
                        {(product.type === 'shoe' || product.type === 'clothes') && product.variants && product.variants.length > 0 && (
                            <>
                                <div className="variants-section">
                                    <h4>Доступные варианты — выберите строку или карточку</h4>
                                    {/* Таблица для десктопа */}
                                    <div className="variants-table-wrapper variants-desktop-only">
                                    <Table className="variants-table" striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th className="sortable" onClick={() => handleSort('size')}>Размер {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                                <th className="sortable" onClick={() => handleSort('color')}>Цвет {sortBy === 'color' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                                <th className="sortable" onClick={() => handleSort('store')}>Магазин {sortBy === 'store' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                                <th className="sortable" onClick={() => handleSort('quantity')}>Кол-во {sortBy === 'quantity' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedVariantsWithIdx.map(({ variant: v, originalIdx }) => {
                                                // Для обуви/одежды считаем наличие только по quantity.
                                                // На проде product_stores может приходить агрегированным и завышать число.
                                                const qty = (v.quantity ?? (Array.isArray(v.product_stores) && v.product_stores.length > 0
                                                    ? v.product_stores.reduce((s, ps) => s + (ps.quantity || 0), 0)
                                                    : 0));
                                                const isSelected = selectedVariantIdx === originalIdx;
                                                const isDisabled = qty === 0;
                                                return (
                                                    <tr
                                                        key={originalIdx}
                                                        className={`variant-row ${isSelected ? 'selected' : ''} ${isDisabled ? 'out-of-stock' : ''}`}
                                                        onClick={() => {
                                                            if (!isDisabled || canAdminEdit) setSelectedVariantIdx(originalIdx);
                                                        }}
                                                        title={canAdminEdit ? 'Редактирование (админ)' : (isDisabled ? 'Нет в наличии' : 'Нажмите для выбора')}
                                                    >
                                                        <td>{v.size || '—'}</td>
                                                        <td>{v.color || '—'}</td>
                                                        <td className="store-cell" title={getStoreName(v)}>{getStoreShort(v)}</td>
                                                        <td>{qty}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                                    {/* Карточки для мобильных */}
                                    <div className="variants-cards-mobile">
                                        {sortedVariantsWithIdx.map(({ variant: v, originalIdx }) => {
                                            // Для обуви/одежды считаем наличие только по quantity.
                                            // (Не суммируем v.product_stores, т.к. на проде оно может быть агрегированным/дублирующим.)
                                            const qty = (v.quantity ?? (Array.isArray(v.product_stores) && v.product_stores.length > 0
                                                ? v.product_stores.reduce((s, ps) => s + (ps.quantity || 0), 0)
                                                : 0));
                                            const isSelected = selectedVariantIdx === originalIdx;
                                            const isDisabled = qty === 0;
                                            return (
                                                <div
                                                    key={originalIdx}
                                                    className={`variant-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'out-of-stock' : ''}`}
                                                    onClick={() => {
                                                        if (!isDisabled || canAdminEdit) setSelectedVariantIdx(originalIdx);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => (canAdminEdit || !isDisabled) && (e.key === 'Enter' || e.key === ' ') && setSelectedVariantIdx(originalIdx)}
                                                >
                                                    <div className="variant-card-row"><strong>Размер:</strong> {v.size || '—'}</div>
                                                    <div className="variant-card-row"><strong>Цвет:</strong> {v.color || '—'}</div>
                                                    <div className="variant-card-row"><strong>Магазин:</strong> {getStoreShort(v)}</div>
                                                    <div className="variant-card-row"><strong>В наличии:</strong> {qty} шт.</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {selectedVariantIdx >= 0 && product.variants[selectedVariantIdx] && (
                                    <div className="selected-variant-info">
                                        <b>Выбран:</b> {product.variants[selectedVariantIdx].size} / {product.variants[selectedVariantIdx].color} — {getStoreName(product.variants[selectedVariantIdx])}
                                    </div>
                                )}
                            </>
                        )}
                        {product.type === 'toy' && product.variants && product.variants.length > 0 && (
                            <>
                                <div className="variants-section">
                                    <h4>Варианты — выберите строку или карточку</h4>
                                    {/* Таблица для десктопа */}
                                    <div className="variants-table-wrapper variants-desktop-only">
                                    <Table className="variants-table" striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th className="sortable" onClick={() => handleSort('name')}>Название {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                                <th className="sortable" onClick={() => handleSort('store')}>Магазин {sortBy === 'store' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                                <th className="sortable" onClick={() => handleSort('quantity')}>Кол-во {sortBy === 'quantity' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
                                                <th>Штрих‑код</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedVariantsWithIdx.map(({ variant: v, originalIdx }) => {
                                                const qty = v.quantity ?? 0;
                                                const isSelected = selectedVariantIdx === originalIdx;
                                                const isDisabled = qty === 0;
                                                return (
                                                    <tr
                                                        key={originalIdx}
                                                        className={`variant-row ${isSelected ? 'selected' : ''} ${isDisabled ? 'out-of-stock' : ''}`}
                                                        onClick={() => {
                                                            if (!isDisabled || canAdminEdit) setSelectedVariantIdx(originalIdx);
                                                        }}
                                                        title={canAdminEdit ? 'Редактирование (админ)' : (isDisabled ? 'Нет в наличии' : 'Нажмите для выбора')}
                                                    >
                                                        <td>{v.name || '—'}</td>
                                                        <td className="store-cell" title={getStoreName(v)}>{getStoreShort(v)}</td>
                                                        <td>{qty}</td>
                                                        <td>{v.barcode || '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                                    {/* Карточки для мобильных */}
                                    <div className="variants-cards-mobile">
                                        {sortedVariantsWithIdx.map(({ variant: v, originalIdx }) => {
                                            const qty = v.quantity ?? 0;
                                            const isSelected = selectedVariantIdx === originalIdx;
                                            const isDisabled = qty === 0;
                                            return (
                                                <div
                                                    key={originalIdx}
                                                    className={`variant-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'out-of-stock' : ''}`}
                                                    onClick={() => {
                                                        if (!isDisabled || canAdminEdit) setSelectedVariantIdx(originalIdx);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => (canAdminEdit || !isDisabled) && (e.key === 'Enter' || e.key === ' ') && setSelectedVariantIdx(originalIdx)}
                                                >
                                                    <div className="variant-card-row"><strong>Название:</strong> {v.name || '—'}</div>
                                                    <div className="variant-card-row"><strong>Магазин:</strong> {getStoreShort(v)}</div>
                                                    <div className="variant-card-row"><strong>В наличии:</strong> {qty} шт.</div>
                                                    <div className="variant-card-row"><strong>Штрих-код:</strong> {v.barcode || '—'}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {selectedVariantIdx >= 0 && product.variants[selectedVariantIdx] && (
                                    <div className="selected-variant-info">
                                        <b>Выбран:</b> {product.variants[selectedVariantIdx].name || '—'} — {getStoreName(product.variants[selectedVariantIdx])}
                                    </div>
                                )}
                            </>
                        )}
                        {canAdminEdit && selectedVariant && selectedVariantId && (
                            <div className="admin-variant-editor mt-4 p-3 border rounded" style={{ background: '#fff' }}>
                                <h5 style={{ marginBottom: 12 }}>Админ: редактирование варианта</h5>

                                <Form.Group className="mb-2">
                                    <Form.Label>Количество</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={0}
                                        value={adminVariantQuantity}
                                        onChange={(e) => setAdminVariantQuantity(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Магазин</Form.Label>
                                    <Form.Select
                                        value={adminVariantStoreId === '' ? '' : String(adminVariantStoreId)}
                                        onChange={(e) => setAdminVariantStoreId(e.target.value)}
                                    >
                                        {product?.type === 'toy' && <option value="">Без магазина</option>}
                                        {(allStores || []).map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.address || s.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <div className="d-flex gap-2">
                                    <Button
                                        variant="success"
                                        onClick={handleAdminSaveVariant}
                                        disabled={adminVariantSaving || !canSaveAdminVariant}
                                    >
                                        {adminVariantSaving ? 'Сохраняем...' : 'Сохранить'}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={handleAdminDeleteVariant}
                                        disabled={adminVariantSaving || !product?.type}
                                    >
                                        Удалить
                                    </Button>
                                </div>
                            </div>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>Количество</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                max={Math.max(1, stock)}
                                value={quantity}
                                onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    if (!isNaN(val)) setQuantity(Math.min(Math.max(1, val), Math.max(1, stock)));
                                }}
                            />
                            {stock > 0 && stock < 999 && (
                                <Form.Text className="text-muted">Доступно: {stock} шт.</Form.Text>
                            )}
                        </Form.Group>
                        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                        <Button 
                            variant="primary"
                            size="lg"
                            className="add-to-basket-button"
                            onClick={handleAddToBasket}
                            disabled={isOutOfStock}
                        >
                            Добавить в корзину
                        </Button>
                        {isOutOfStock && (
                            <div style={{ color: 'red', marginTop: 8, fontWeight: 500 }}>
                                Товара нет в наличии
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Форма для отзыва */}
            <Row className="mt-5">
                <Col md={12} lg={8}>
                    <div className="rating-form">
                        <h3>Оставить отзыв</h3>
                        {ratingError && <Alert variant="danger">{ratingError}</Alert>}
                        {ratingSuccess && <Alert variant="success">{ratingSuccess}</Alert>}
                        <Form onSubmit={handleRatingSubmit}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ваша оценка</Form.Label>
                                <div className="rating-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <FaStar
                                            key={star}
                                            size={30}
                                            color={star <= rating ? "#ffc107" : "#e4e5e9"}
                                            onClick={() => setRating(star)}
                                            style={{ cursor: 'pointer', marginRight: '5px' }}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Комментарий</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Расскажите о вашем опыте использования товара"
                                />
                            </Form.Group>
                            <Button variant="primary" type="submit">
                                Отправить отзыв
                            </Button>
                        </Form>
                    </div>
                </Col>
            </Row>

            {/* Отображение существующих отзывов */}
            {product.ratings && product.ratings.length > 0 && (
                <Row className="mt-5">
                    <Col md={12}>
                        <h3>Отзывы покупателей</h3>
                        <div className="ratings-list">
                            {product.ratings.map((rating, index) => (
                                <div key={index} className="rating-item mb-4 p-3 border rounded">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="rating-header">
                                            <div className="rating-stars mb-2">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <FaStar
                                                        key={star}
                                                        size={20}
                                                        color={star <= rating.score ? "#ffc107" : "#e4e5e9"}
                                                    />
                                                ))}
                                            </div>
                                            <div className="rating-user">
                                                <strong>{rating.userName}</strong>
                                                <small className="text-muted ms-2">
                                                    {new Date(rating.createdAt).toLocaleDateString()}
                                                </small>
                                            </div>
                                        </div>
                                        {canAdminEdit && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleAdminDeleteRating(rating.id)}
                                            >
                                                Удалить
                                            </Button>
                                        )}
                                    </div>
                                    {rating.comment && (
                                        <div className="rating-comment mt-2">
                                            {rating.comment}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            )}

            {/* Отображение среднего рейтинга */}
            {product.averageRating > 0 && (
                <Row className="mt-3">
                    <Col md={12}>
                        <div className="average-rating">
                            <h4>Средняя оценка: {product.averageRating.toFixed(1)}</h4>
                            <div className="rating-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <FaStar
                                        key={star}
                                        size={24}
                                        color={star <= Math.round(product.averageRating) ? "#ffc107" : "#e4e5e9"}
                                    />
                                ))}
                            </div>
                            <small className="text-muted">
                                На основе {product.ratings?.length || 0} отзывов
                            </small>
                        </div>
                    </Col>
                </Row>
            )}
        </Container>
    );
});

export default ProductPage;